import { Button, Form, Input, Modal, Select, Table, Tag, Typography, message } from 'antd';
import * as Component from 'components';
import { BASE_URL } from 'constants/api';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { playlistApiService } from '../services/playlistApiService';
import { FeedValidationResult, PlaylistInterface } from '../types/playlistType';
import './PlaylistDetail.css';

const { Text, Link } = Typography;
const MRSS_URL_FORMAT = `${BASE_URL}/api/v1/playlist/{id}/mrss/{service_name}`;

export const PlaylistDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [playlistData, setPlaylistData] = useState<PlaylistInterface>();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<FeedValidationResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [recoveringIds, setRecoveringIds] = useState<Set<number>>(new Set());
  const [recoverAllProgress, setRecoverAllProgress] = useState<string | null>(null);

  const fetchPlaylist = async (): Promise<void> => {
    const playlist = await playlistApiService.fetchById(Number(id));
    form.setFieldsValue(playlist);
    setPlaylistData(playlist);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPlaylist().then();
  }, [id, form]);

  const handleValidateFeed = async () => {
    setValidating(true);
    try {
      const result = await playlistApiService.validateFeed(Number(id));
      if (result.invalid_count === 0) {
        message.success(`All ${result.total_count} items valid`);
      } else {
        setValidationResult(result);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Feed validation failed:', error);
    } finally {
      setValidating(false);
    }
  };

  const handleRecover = async (videoId: number) => {
    setRecoveringIds(prev => new Set(prev).add(videoId));
    try {
      await playlistApiService.recoverVideo(videoId);
      message.success('Recovery initiated');
    } catch (error) {
      console.error('Recovery failed:', error);
    } finally {
      setRecoveringIds(prev => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
    }
  };

  const handleRecoverAll = async () => {
    if (!validationResult) return;
    const recoverableItems = validationResult.invalid_items.filter(item => item.video_id !== null);
    const total = recoverableItems.length;

    for (let i = 0; i < total; i++) {
      setRecoverAllProgress(`Recovering ${i + 1}/${total}...`);
      try {
        await playlistApiService.recoverVideo(recoverableItems[i].video_id!);
      } catch (error) {
        console.error(`Failed to recover: ${recoverableItems[i].title}`, error);
      }
    }
    setRecoverAllProgress('Complete');

    // Re-validate to refresh results
    try {
      const result = await playlistApiService.validateFeed(Number(id));
      if (result.invalid_count === 0) {
        setModalOpen(false);
        message.success(`All ${result.total_count} items valid`);
      } else {
        setValidationResult(result);
      }
    } catch (error) {
      console.error('Re-validation failed:', error);
    }
    setRecoverAllProgress(null);
  };

  const recoverableCount = validationResult?.invalid_items.filter(
    item => item.video_id !== null
  ).length ?? 0;

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Content ID',
      dataIndex: 'content_id',
      key: 'content_id',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: FeedValidationResult['invalid_items'][number]) => {
        if (record.video_id !== null) {
          return (
            <Button
              type="link"
              loading={recoveringIds.has(record.video_id)}
              onClick={() => handleRecover(record.video_id!)}
            >
              Recover
            </Button>
          );
        }
        const path = record.content_type === 'movie'
          ? `/movie/edit/${record.content_id}`
          : `/episode/edit/${record.content_id}`;
        return (
          <Button
            type="link"
            onClick={() => window.open(path, '_blank')}
          >
            View Content
          </Button>
        );
      },
    },
  ];

  return (
    <>
      {isLoading ? (
        <Component.LoadingSpinnerComponent />
      ) : (
        <div>
          <div className="playlist-details-page">
            <Form
              form={form}
              layout="vertical"
              className="playlist-details-form"
              initialValues={playlistData}
            >
              <Form.Item name="title" label="Title">
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input />
              </Form.Item>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'manual', label: 'manual' },
                    { value: 'dynamic', label: 'dynamic' },
                  ]}
                  disabled={true}
                />
              </Form.Item>
              <Form.Item name="created" label="Created">
                <Input readOnly />
              </Form.Item>
            </Form>
          </div>

          <div>
            <div className="playlist-sources">
              <h3>Playlist MRSS Sources</h3>
            </div>
            <div className="playlist-mrss" style={{ marginTop: '30px' }}>
              <Text strong style={{ marginLeft: '30px', marginRight: '10px' }}>
                Zype MRSS Feed URL:
              </Text>
              <Link
                href={MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'zype')}
                target="_blank"
              >
                {MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'zype')}
              </Link>
              <br />
              <Text strong style={{ marginLeft: '30px', marginRight: '10px' }}>
                Amagi MRSS Feed URL:
              </Text>
              <Link
                href={MRSS_URL_FORMAT.replace('{id}', String(id)).replace(
                  '{service_name}',
                  'amagi'
                )}
                target="_blank"
              >
                {MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'amagi')}
              </Link>
              <br />
              <Text strong style={{ marginLeft: '30px', marginRight: '10px' }}>
                Xumo MRSS Feed URL :
              </Text>
              <Link
                href={MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'xumo')}
                target="_blank"
              >
                {MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'xumo')}
              </Link>
              <br />
              <Text strong style={{ marginLeft: '30px', marginRight: '10px' }}>
                Vimai MRSS Feed URL :
              </Text>
              <Link
                href={MRSS_URL_FORMAT.replace('{id}', String(id)).replace(
                  '{service_name}',
                  'vimai'
                )}
                target="_blank"
              >
                {MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'vimai')}
              </Link>
              <br />
              <Text strong style={{ marginLeft: '30px', marginRight: '10px' }}>
                ODK MRSS Feed URL :
              </Text>
              <Link
                href={MRSS_URL_FORMAT.replace('{id}', String(id)).replace(
                  '{service_name}',
                  'odk'
                )}
                target="_blank"
              >
                {MRSS_URL_FORMAT.replace('{id}', String(id)).replace('{service_name}', 'odk')}
              </Link>
            </div>
          </div>
          <div className="playlist-detail-update-button" style={{ marginTop: '30px' }}>
            <Component.ColorButtonComponent
              text={'Update'}
              onClick={async () => {
                const newPlaylist = form.getFieldsValue();
                await playlistApiService.update(Number(id), newPlaylist);
                navigate('/playlists/');
              }}
            />
            <Button
              type="primary"
              loading={validating}
              onClick={handleValidateFeed}
              style={{ marginLeft: '10px', backgroundColor: '#1890ff' }}
            >
              Validate Feed
            </Button>
          </div>

          <Modal
            title="Feed Validation Result"
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            footer={null}
            width={700}
          >
            {validationResult && (
              <>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <Tag>Total: {validationResult.total_count}</Tag>
                  <Tag color="success">Valid: {validationResult.valid_count}</Tag>
                  <Tag color="error">Invalid: {validationResult.invalid_count}</Tag>
                </div>
                <Table
                  dataSource={validationResult.invalid_items}
                  columns={columns}
                  rowKey="content_id"
                  pagination={false}
                  size="small"
                  title={() => <Text strong>Invalid Items</Text>}
                />
                {recoverableCount >= 2 && (
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <Button
                      type="primary"
                      onClick={handleRecoverAll}
                      disabled={recoverAllProgress !== null}
                    >
                      {recoverAllProgress ?? 'Recover All'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </Modal>
        </div>
      )}
    </>
  );
};
